import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Leaf, Users, Heart, CheckCircle, AlertTriangle, Recycle, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';

const HSEPage = () => {
  const hseCommitments = [
    {
      icon: <AlertTriangle className="w-8 h-8" />,
      title: 'Risk Control',
      description: 'Comprehensive risk assessment and control measures for all operations.',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Safe Working Conditions',
      description: 'Ensuring safe working environments through rigorous standards and protocols.',
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Employee Training',
      description: 'Continuous training and certification programs for all personnel.',
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Compliance',
      description: 'Full compliance with local and international HSE regulations.',
    },
  ];

  const environmentalCommitments = [
    'Minimize environmental impact through responsible practices',
    'Proper waste management and disposal procedures',
    'Regular environmental impact assessments',
    'Use of environmentally friendly materials where possible',
    'Spill prevention and response protocols',
    'Carbon footprint reduction initiatives',
  ];

  const csrInitiatives = [
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Community Engagement',
      description: 'Active participation in community development projects and dialogue with local stakeholders.',
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'Skills Transfer',
      description: 'Training and development programs for local workforce to build indigenous capacity.',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Local Content Development',
      description: 'Prioritizing local suppliers and contractors to stimulate economic growth.',
    },
    {
      icon: <Recycle className="w-6 h-6" />,
      title: 'Sustainable Practices',
      description: 'Implementing sustainable business practices that benefit future generations.',
    },
  ];

  const safetyStats = [
    { number: '0', label: 'Lost Time Injuries' },
    { number: '100%', label: 'Safety Compliance' },
    { number: '500+', label: 'Training Hours/Year' },
    { number: '24/7', label: 'Emergency Response' },
  ];

  return (
    <div data-testid="hse-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FF6B00' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-[#FF6B00] text-white text-xs uppercase tracking-widest font-bold mb-6">
              HSE & CSR
            </span>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold text-white uppercase leading-tight tracking-tight mb-6">
              Safety First, Always
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Our commitment to Health, Safety, Environment, and Corporate Social Responsibility is at the core of everything we do.
            </p>
          </div>
        </div>
      </section>

      {/* Safety Stats */}
      <section className="py-12 bg-[#FF6B00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {safetyStats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl md:text-5xl font-bold text-white font-['Barlow_Condensed']">
                  {stat.number}
                </div>
                <div className="text-sm text-white/80 uppercase tracking-wider mt-2">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Health & Safety Policy */}
      <section className="py-20 md:py-32" data-testid="health-safety-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
                Our Commitment
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4 mb-6">
                Health & Safety Policy
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                At Godston Projects, we believe that every employee has the right to work in a safe and healthy environment. Our HSE policy ensures that safety is never compromised in the pursuit of productivity or profit.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {hseCommitments.map((item, index) => (
                  <div 
                    key={index}
                    className="bg-[#F5F5F5] p-6 hover:bg-slate-100 transition-colors"
                    data-testid={`hse-commitment-${index}`}
                  >
                    <div className="text-[#FF6B00] mb-4">{item.icon}</div>
                    <h4 className="font-['Barlow_Condensed'] font-bold text-[#0A0A0A] uppercase mb-2">
                      {item.title}
                    </h4>
                    <p className="text-slate-600 text-sm">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwZW5naW5lZXJpbmclMjBjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBzYWZldHklMjBoZWxtZXR8ZW58MHx8fHwxNzczODU0ODMxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Safety equipment"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0A0A0A] p-6 text-white">
                <Shield className="w-8 h-8 text-[#FF6B00] mb-2" />
                <div className="text-sm uppercase tracking-wider">Zero Tolerance</div>
                <div className="text-xs text-slate-400">for Safety Violations</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Environmental Commitment */}
      <section className="py-20 md:py-32 bg-[#F5F5F5]" data-testid="environmental-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-[4/3] bg-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&auto=format&fit=crop&q=60"
                  alt="Environmental commitment"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
                Environmental Stewardship
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4 mb-6">
                Environmental Commitment
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We recognize our responsibility to protect the environment and are committed to minimizing the environmental impact of our operations through sustainable practices.
              </p>
              <ul className="space-y-4">
                {environmentalCommitments.map((commitment, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Leaf className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{commitment}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CSR Section */}
      <section className="py-20 md:py-32" data-testid="csr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
              Giving Back
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4">
              Corporate Social Responsibility
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto mt-6">
              We are committed to making a positive impact in the communities where we operate through meaningful CSR initiatives.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {csrInitiatives.map((initiative, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 p-8 hover:border-[#FF6B00] transition-colors text-center"
                data-testid={`csr-initiative-${index}`}
              >
                <div className="w-16 h-16 bg-[#FF6B00]/10 flex items-center justify-center mx-auto mb-6">
                  <span className="text-[#FF6B00]">{initiative.icon}</span>
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0A0A0A] uppercase mb-3">
                  {initiative.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {initiative.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
              Compliance
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mt-4">
              Certifications & Standards
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {['ISO 9001:2015', 'ISO 14001:2015', 'OHSAS 18001', 'NCDMB Certified'].map((cert, index) => (
              <div 
                key={index}
                className="bg-slate-900 p-8 text-center hover:bg-slate-800 transition-colors"
              >
                <CheckCircle className="w-10 h-10 text-[#FF6B00] mx-auto mb-4" />
                <span className="text-white font-['Barlow_Condensed'] font-bold uppercase">
                  {cert}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mb-6">
            Learn More About Our Practices
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-10">
            Contact us to learn more about our HSE policies and CSR initiatives, or to request our HSE documentation.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="hse-page-cta"
              className="bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold px-10 py-6 text-base btn-glow"
            >
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HSEPage;
